import React from 'react';
import { useLocator } from '../../contexts/franchise/locator-provider';
import FranchiseSearchInput from './franchise-search-input';
import type { SearchInputAddressComponents } from './franchise-search-input';
import { PHONE_NUMBER, PHONE_NUMBER_DISPLAY, AVERAGE_RATE_THRESHOLD, CHANGE_LOCATION } from '../../constants/constants';
import useNearbyFranchises from '../../hooks/use-nearby-franchises';
import { colors } from '../primitives/colors';
import Divider from '../primitives/divider';
import { Drawer, DrawerContent } from '../primitives/drawer';
import RowSet from '../primitives/grid/row-set';
import Skeleton from '../primitives/skeleton';
import { StyledButtonAsLink } from '../primitives/styled-button';
import StyledPillButton from '../primitives/styled-pill-button';
import { breakpoints } from '../primitives/tokens';
import Typography from '../primitives/typography';
import { GeocoderReturnable } from '../../api-utils/geocode';
import Container from '../primitives/grid/container';
import CloseButton from '../atoms/CloseButton';
import Logo from '../atoms/Logo';
import { RatingSummary } from '../atoms/RatingSummary';

interface ChangeLocationDrawerProps {
    visible: boolean
    onClose: () => void
}

const ChangeLocationDrawer = ({ visible, onClose }: ChangeLocationDrawerProps): JSX.Element => {
    const [query, setQuery] = React.useState<GeocoderReturnable|null>(null);
    // might be only relevant while we decide which location detection provider to use
    // gets the address the user entered when he ultimately selects a franchise
    const [
        userSelectedGeoLocation,
        setUserSelectedGeoLocation,
    ] = React.useState<SearchInputAddressComponents|null>(null);

    const { setFranchise, nearby, geo: locatorGeo } = useLocator();

    const {
        data: searchedFranchises,
        isLoading,
        announcements,
    } = useNearbyFranchises(query && (query.latitude && query.longitude) ? {
        latitude: query.latitude,
        longitude: query.longitude,
        zip: query?.zip || null,
        locationType: query.locationType || null,
        country: query?.country || null,
        provider: 'SEARCH',
    } : null);

    // If no query has been made, use the franchises we already found via the locator
    const franchises = query ? searchedFranchises : nearby;

    return (
        <Drawer
            open={visible}
            onOpenChange={() => {
                onClose();
            }}
        >
            <DrawerContent
                placement="left"
                size="xl"
                css={{
                    overflowY: 'auto',
                    outline: 'none',
                }}
            >
                <Container css={{
                    padding: '0 24px',
                    [breakpoints.mb]: {
                        padding: '0 16px',
                    },
                }}
                >
                    <div css={{
                        borderBottom: `1px solid ${colors.grey200}`,
                        paddingTop: '24px',
                        paddingBottom: '24px',
                    }}
                    >
                        <div css={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '16px',
                            [breakpoints.mb]: {
                                justifyContent: 'flex-end',
                            },
                        }}
                        >
                            <div css={{
                                width: '100%',
                                display: 'block',
                                maxWidth: '220px',
                                [breakpoints.mb]: {
                                    display: 'none',
                                },
                            }}
                            >
                                <Logo />
                            </div>
                            <CloseButton
                                css={{
                                    position: 'relative',
                                }}
                                onClick={onClose}
                            />
                        </div>
                        <div css={{
                            marginTop: '8px',
                            marginBottom: '8px',
                            [breakpoints.mb]: {
                                display: 'none',
                            },
                        }}
                        >
                            <StyledPillButton onClick={onClose}>‹ Back</StyledPillButton>
                        </div>
                        <Typography
                            fontSize="lg"
                            fontWeight="medium"
                            css={{
                                marginBottom: '20px',
                            }}
                        >
                            {CHANGE_LOCATION}
                        </Typography>
                        <FranchiseSearchInput
                            className="change-location-cta"
                            value={query?.address || ''}
                            onChange={(address) => {
                                setQuery(address);
                                setUserSelectedGeoLocation(address);
                            }}
                        >
                            Search
                        </FranchiseSearchInput>
                    </div>
                </Container>
                <Container css={{
                    padding: '0 24px',
                    [breakpoints.mb]: {
                        padding: '0 16px',
                    },
                }}
                >
                    {isLoading && Array.from(Array(5)).map((_, idx) => (
                        <React.Fragment
                            /* eslint-disable-next-line react/no-array-index-key */
                            key={idx}
                        >
                            <RowSet
                                breakpoints={{ dt: { between: 10 } }}
                                css={{
                                    padding: '24px 0',
                                    [breakpoints.mb]: {
                                        padding: '16px 24px',
                                    },
                                }}
                            >
                                <Skeleton css={{ minHeight: '67px' }} />
                            </RowSet>
                            {idx !== franchises.slice(0, 5).length && (
                                <Divider />
                            )}
                        </React.Fragment>
                    ))}
                    {!isLoading && announcements.filter(x => !!x.search_box_copy).map(({ search_box_copy }) => (
                        <div
                            css={{
                                background: colors.primaryOrange,
                                color: colors.shadesWhite,
                                padding: '16px 0',
                                fontSize: '12px',
                                [breakpoints.mb]: {
                                    padding: '12px 0',
                                },
                            }}
                            dangerouslySetInnerHTML={{
                                __html: search_box_copy || '',
                            }}
                        />
                    ))}
                    {!isLoading && franchises?.length === 0 && (
                        <RowSet
                            breakpoints={{ dt: { between: 10 } }}
                            css={{
                                padding: '24px 0',
                                [breakpoints.mb]: {
                                    padding: '16px 0',
                                },
                            }}
                        >
                            <Typography fontSize={{ tb: 'lg', dt: 'xl' }} fontWeight="bold">
                                SERVPRO National Call Center
                            </Typography>
                            <div css={{
                                maxWidth: '250px',
                            }}
                            >
                                <StyledButtonAsLink to={`tel:${PHONE_NUMBER}`}>
                                    {PHONE_NUMBER_DISPLAY}
                                </StyledButtonAsLink>
                            </div>
                        </RowSet>
                    )}
                    {!isLoading && franchises?.length > 0 && (
                        franchises.slice(0, 5).map(({ data: franchiseData, isLoading: franchiseLoading }, idx) => {
                            const { name, reviewCount, averageRating } = franchiseData || {};

                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <React.Fragment key={idx}>
                                    <RowSet
                                        breakpoints={{ dt: { between: 10 } }}
                                        css={{
                                            padding: '24px 0',
                                            [breakpoints.mb]: {
                                                padding: '16px 0',
                                            },
                                        }}
                                    >
                                        <Skeleton css={{ minHeight: '67px' }}>
                                            {(!franchiseLoading) && (
                                                <div css={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                }}
                                                >
                                                    <Typography
                                                        fontSize={{ tb: 'lg', dt: 'xl' }}
                                                        fontWeight="bold"
                                                        css={{
                                                            maxWidth: '50%',
                                                            marginRight: '8px',
                                                        }}
                                                    >
                                                        {name}
                                                    </Typography>
                                                    <div css={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        [breakpoints.mb]: {
                                                            minWidth: 'unset',
                                                        },
                                                    }}
                                                    >
                                                        <Typography
                                                            fontSize={{ dt: 'base', tb: 'sm' }}
                                                            fontWeight="medium"
                                                            css={{
                                                                padding: '8px 28px',
                                                                backgroundColor: colors.primaryOrange,
                                                                color: colors.shadesWhite,
                                                                marginBottom: '8px',
                                                                [breakpoints.tb]: {
                                                                    padding: '8px 16px',
                                                                },
                                                            }}
                                                        >
                                                            <button
                                                                type="button"
                                                                className="change-location-select-cta"
                                                                onClick={() => {
                                                                    /*
                                                                    * 'userSelectedGeoLocation' might only
                                                                    * be relevant while we decide which location
                                                                    * detection provider to use
                                                                    * */
                                                                    if (franchiseData && userSelectedGeoLocation) {
                                                                        // This condition is relevant when the user enters an address in the search box

                                                                        setFranchise(
                                                                            franchiseData,
                                                                            userSelectedGeoLocation,
                                                                        );
                                                                        onClose();
                                                                    } else if (franchiseData && locatorGeo) {
                                                                        // This condition is relevant when a user has not entered an address in the search box,
                                                                        // But we still show some nearby franchises anyway
                                                                        setFranchise(
                                                                            franchiseData,
                                                                            {
                                                                                latitude: locatorGeo.latitude,
                                                                                longitude: locatorGeo.longitude,
                                                                                city: locatorGeo.city,
                                                                                state: locatorGeo.state,
                                                                                stateShort: locatorGeo.stateShort,
                                                                                zip: locatorGeo.zip || '',
                                                                                country: locatorGeo.country,
                                                                            },
                                                                        );
                                                                        onClose();
                                                                    }
                                                                }}
                                                            >
                                                                Select This Location
                                                            </button>
                                                        </Typography>
                                                        {reviewCount !== 0 && !!averageRating && averageRating >= AVERAGE_RATE_THRESHOLD && (
                                                            <RatingSummary
                                                                smallSize
                                                                rating={averageRating}
                                                                count={reviewCount}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </Skeleton>
                                    </RowSet>
                                    <Divider />
                                </React.Fragment>
                            );
                        }))}
                </Container>
            </DrawerContent>
        </Drawer>
    );
};

export default ChangeLocationDrawer;
